/**
 * UC-14: ExecuteSocAction
 * TOGAF Application ABB — emergency SOC response without UI deps.
 */
import type { IAppLogger } from '../../domain/ports/IAppLogger';
import type {
  ExecuteSocActionInput,
  ExecuteSocActionOutput,
  SocActionType,
} from '../dto/ExecuteSocActionDto';

export const SOC_ACTION_COSTS: Record<SocActionType, number> = {
  contain: 2000,
  ransom: 50000,
  restore: 500,
};

export const SOC_THREAT_REDUCTION: Record<SocActionType, number> = {
  contain: 20,
  ransom: 50,
  restore: 5,
};

const SUCCESS_MESSAGES: Record<SocActionType, string> = {
  contain: 'Breach Contained. Threat Reduced.',
  ransom: 'Ransom Paid. Systems Decrypted.',
  restore: 'Logs Flushed. Minor Threat Reduction.',
};

export interface ExecuteSocActionDependencies {
  logger?: IAppLogger;
}

export class ExecuteSocActionUseCase {
  constructor(private readonly deps: ExecuteSocActionDependencies = {}) {}

  execute(input: ExecuteSocActionInput): ExecuteSocActionOutput {
    const { action, stats, hasJumpGate, activeEventId } = input;

    if (!hasJumpGate) {
      this.deps.logger?.log('debug', 'soc.access_denied', { action });
      return {
        ok: false,
        reason: 'jump_gate_required',
        newsMessage: 'ACCESS DENIED: SOC requires Admin Jump Gate.',
        newsType: 'negative',
      };
    }

    const cost = SOC_ACTION_COSTS[action];

    if (stats.budget < cost) {
      this.deps.logger?.log('debug', 'soc.insufficient_funds', {
        action,
        cost,
        budget: stats.budget,
      });
      return {
        ok: false,
        reason: 'insufficient_funds',
        newsMessage: 'Insufficient funds for SOC action.',
        newsType: 'negative',
        playErrorSfx: true,
      };
    }

    const threatReduction = SOC_THREAT_REDUCTION[action];
    const newThreat = Math.max(0, stats.threat - threatReduction);
    const newBudget = stats.budget - cost;
    const clearActiveEvent = action === 'ransom' && activeEventId === 'ransomware';

    this.deps.logger?.log('debug', 'soc.action_executed', {
      action,
      cost,
      threatBefore: stats.threat,
      threatAfter: newThreat,
      clearActiveEvent,
    });

    return {
      ok: true,
      stats: { budget: newBudget, threat: newThreat },
      clearActiveEvent,
      newsMessage: SUCCESS_MESSAGES[action],
      newsType: 'neutral',
    };
  }
}